/**
 * Meta Ads OAuth Callback Page
 * Handles redirect from Meta OAuth dialog, exchanges code for token.
 */
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { postRequestWithAuth } from '~/lib/api';
import { useAuth } from '~/contexts/AuthContext';
import {
  getWizardOAuthState,
  clearWizardOAuthState,
} from '~/lib/wizard-oauth-state';

type CallbackState = 'processing' | 'error';

export default function MetaAdsCallback() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('Connecting to Meta Ads...');
  const [callbackState, setCallbackState] = useState<CallbackState>('processing');
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const processingRef = useRef(false);

  useEffect(() => {
    const processCallback = async () => {
      // Prevent double execution from React Strict Mode
      if (processingRef.current) return;
      processingRef.current = true;

      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorReason = searchParams.get('error_reason');
      const errorDescription = searchParams.get('error_description');

      // Handle OAuth error (user denied permission)
      if (errorParam) {
        const wizardState = getWizardOAuthState();
        if (wizardState) {
          // Return to wizard with error state
          clearWizardOAuthState();
          navigate('/app/dashboard?metaOAuthReturn=error', { replace: true });
          return;
        }
        // Standard error display for non-wizard OAuth
        const errorMsg = errorDescription || errorReason || 'Authorization was denied';
        setError(errorMsg);
        setCallbackState('error');
        return;
      }

      if (!code || !state) {
        setError('Missing required parameters');
        setCallbackState('error');
        return;
      }

      setStatus('Exchanging token...');
      try {
        const response = await postRequestWithAuth('/api/v1/meta-ads/callback', {
          code,
          state,
        });

        // Refresh user data
        await refreshAuth();

        // Check if returning to wizard (vs. dashboard OAuth)
        const wizardState = getWizardOAuthState();
        if (wizardState) {
          clearWizardOAuthState();
          // Return to dashboard with wizard-specific params
          navigate(
            `/app/dashboard?metaOAuthReturn=success&metaConnectionId=${response.connection_id}`,
            { replace: true }
          );
        } else {
          // Standard dashboard redirect (non-wizard OAuth)
          navigate('/app/dashboard?newConnection=meta-ads', { replace: true });
        }
      } catch (err: any) {
        const detail = err.response?.data?.detail || 'Failed to connect to Meta Ads';
        setError(detail);
        setCallbackState('error');
      }
    };

    processCallback();
  }, [searchParams, refreshAuth, navigate]);

  // Error state
  if (callbackState === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-100">
        <div className="card bg-base-200 w-96">
          <div className="card-body">
            <h2 className="card-title text-error">Connection Failed</h2>
            <p className="text-sm text-base-content/70">{error}</p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => navigate('/app/dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Processing state
  return (
    <div className="flex items-center justify-center min-h-screen bg-base-100">
      <div className="text-center">
        <span className="loading loading-spinner loading-lg" />
        <p className="mt-4 text-base-content/70">{status}</p>
      </div>
    </div>
  );
}
